import { useState, useRef, useEffect, useCallback } from 'react';
import { UseFormSetError, UseFormSetValue } from 'react-hook-form';
import {
  useSpeechRecognition,
  SpeechRecognitionError,
} from '../../../hooks/useSpeechRecognition';

interface UseVoiceInputProps {
  currentValue: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setError: UseFormSetError<any>;
  fieldName: string;
}

interface UseVoiceInputReturn {
  isListening: boolean;
  isProcessingVoice: boolean;
  isSupported: boolean;
  handleVoiceInput: () => void;
  stopRecordingAndCommit: (callback?: () => void) => void;
}

export const useVoiceInput = ({
  currentValue,
  setValue,
  setError,
  fieldName,
}: UseVoiceInputProps): UseVoiceInputReturn => {
  const [isProcessingVoice, setIsProcessingVoice] = useState<boolean>(false);
  const [accumulatedTranscript, setAccumulatedTranscript] =
    useState<string>('');

  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const speechSessionIdRef = useRef<string>('');
  const transcriptRef = useRef<string>('');

  // Generate unique session ID for each speech session
  const generateSessionId = useCallback(
    () => Date.now().toString() + Math.random().toString(36).substr(2, 9),
    [],
  );

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    },
    [],
  );

  // Simple accumulation that preserves partial sentences
  const processSpeechResult = useCallback(
    (transcript: string, sessionId: string) => {
      if (!transcript.trim() || sessionId !== speechSessionIdRef.current) {
        return;
      }

      // Always append to existing transcript for continuous listening
      setAccumulatedTranscript((prev) => {
        if (!prev) {
          return transcript.trim();
        }

        // For continuous listening, we want to append, not replace
        return `${prev} ${transcript.trim()}`;
      });

      // Keep ref in sync
      transcriptRef.current = transcriptRef.current
        ? `${transcriptRef.current} ${transcript.trim()}`
        : transcript.trim();
    },
    [],
  );

  // Speech recognition hook
  const {
    isListening,
    isSupported,
    startListening,
    stopListening,
    clearError: clearSpeechError,
    resetRecognition,
  } = useSpeechRecognition(
    {
      continuous: true,
      interimResults: false, // Disable interim to avoid complexity
      lang: 'en-US',
    },
    {
      onResult: (transcript: string, isFinal: boolean) => {
        if (isFinal) {
          processSpeechResult(transcript, speechSessionIdRef.current);
        }
      },
      onError: (error: SpeechRecognitionError) => {
        setError(fieldName, {
          type: 'manual',
          message: error.message,
        });
        setIsProcessingVoice(false);
      },
      onEnd: () => {
        // Try state first, then ref for reliability
        let finalTranscript = accumulatedTranscript.trim();

        if (!finalTranscript) {
          finalTranscript = transcriptRef.current.trim();
        }

        if (finalTranscript) {
          const newValue = currentValue
            ? `${currentValue.trim()} ${finalTranscript}`
            : finalTranscript;
          setValue(fieldName, newValue);
        }

        setIsProcessingVoice(false);

        // Clear session data after delay
        setTimeout(() => {
          speechSessionIdRef.current = '';
          setAccumulatedTranscript('');
          transcriptRef.current = '';
        }, 1000);
      },
    },
  );

  // Voice input functionality
  const handleVoiceInput = (): void => {
    if (!isSupported) {
      setError(fieldName, {
        type: 'manual',
        message: 'Speech recognition is not supported in this browser',
      });
      return;
    }

    if (isListening) {
      // Stop and commit any accumulated transcript
      if (accumulatedTranscript.trim() || transcriptRef.current.trim()) {
        const transcript =
          accumulatedTranscript.trim() || transcriptRef.current.trim();
        const newValue = currentValue
          ? `${currentValue.trim()} ${transcript}`
          : transcript;
        setValue(fieldName, newValue);
      }

      stopListening();
    } else {
      // Start listening
      speechSessionIdRef.current = generateSessionId();

      // Clear any existing timeout
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }

      // Clear previous errors and state
      clearSpeechError();
      setIsProcessingVoice(false);
      setAccumulatedTranscript('');
      transcriptRef.current = '';

      // Start recognition
      resetRecognition();
      startListening();
    }
  };

  // Stop recording and commit transcript, then execute callback
  const stopRecordingAndCommit = useCallback(
    (callback?: () => void): void => {
      if (!isListening) {
        // Not recording, execute callback immediately
        callback?.();
        return;
      }

      // Stop and commit any accumulated transcript
      if (accumulatedTranscript.trim() || transcriptRef.current.trim()) {
        const transcript =
          accumulatedTranscript.trim() || transcriptRef.current.trim();
        const newValue = currentValue
          ? `${currentValue.trim()} ${transcript}`
          : transcript;
        setValue(fieldName, newValue);
      }

      stopListening();

      // Execute callback after a brief delay to ensure setValue completes
      if (callback) {
        setTimeout(callback, 500);
      }
    },
    [
      isListening,
      accumulatedTranscript,
      currentValue,
      fieldName,
      setValue,
      stopListening,
    ],
  );

  return {
    isListening,
    isProcessingVoice,
    isSupported,
    handleVoiceInput,
    stopRecordingAndCommit,
  };
};
